import classnames from 'classnames';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import {
  AppClient,
  Auth0SamlConnection,
  BrandingUrl,
  // Auth0Tenant,
  LearningProvider,
  makeAppClient
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import {
  Entry,
  RepositoryEntries,
  RepositoryEntrySet
} from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { RepositoryPutEntry } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { RadioItem } from '../../../../../ui/widgets/radio-item/radio-item';
import { SelectOption } from '../../../../../ui/widgets/select/select';
import {
  newNonEmptyTextField,
  newRegexTextField,
  newRegexTextFieldWithStopWords
} from '../../../../../ui/widgets/text-input/text-field';
import * as FileUtility from '../../../../../util/file-repos-utility';
import {
  CheckItemObserver,
  deleteHandler,
  DisplayCheckItem,
  MultiSelectContainer,
  DisplayText,
  // displayText,
  ImgObserver,
  MultiSelectObserver,
  renderButtons,
  SelectObserver,
  TextInputObserver
} from '../../../common/editor';
import { ImageFileManager, SelectedFileSpec } from '../../image-file-manager/image-file-manager';

const styles = require('../../../common/editor.css');

/// SS-692 banned client domain names
const disallowedDomainNames = [
  'skillseeker',
  'uat',
  'dev',
  'test',
  'prod',
  'auth',
  'admin',
  'api',
  'reports',
  'payments',
  'billing'
];

const defaultProviderId = 'TIA-ONLINE';

/// Can't use "file://../../../assets/images/skillseeker-logo.jpg" - "Not allowed to load local resource"
const defaultLogo =
  'https://com-hx-futurefit-uat-resources.s3-ap-southeast-2.amazonaws.com/skillseeker/icons/SkillSeeker+logo_nopad.jpg';

/// TODO: Read from auth0tenant
/** @deprecated */
const defaultLoginBackground =
  'https://com-hx-futurefit-uat-resources.s3-ap-southeast-2.amazonaws.com/skillseeker/site-images/splash+screen+w1800.png';

interface RestrictedFields {
  name?: JSX.Element;
  domainName?: JSX.Element;
  recommendationsVisible?: JSX.Element;
  providers?: JSX.Element;
  displayHeadlineRatio?: JSX.Element;
  displayHeadlineText?: JSX.Element;
  singleTabRecommendations?: JSX.Element;
  allowSelfAssessment?: JSX.Element;
}

interface AppClientEditorProps {
  debug?: boolean;
  item?: WithDbId<AppClient>;
  learningProviders: WithDbId<LearningProvider>[];
  ssoConnections: WithDbId<Auth0SamlConnection>[];
  repository: RepositoryEntrySet;
  onLoadClientRepository?(repos: RepositoryEntrySet, clientDomainName: string): Promise<RepositoryEntrySet>;
  onUpload(req: RepositoryPutEntry): Promise<Entry>;
  onCreate(item: AppClient, createWithGlobalGroup: boolean): Promise<void>;
  onUpdate?(item: WithDbId<AppClient>): Promise<void>;
  onCancel(): void;
  /// TODO: Change API (client and server) to prevent updates of specific fields (eg. auth0, domain and ?client name fields) for client-admin
  role: 'client-admin' | 'super';
}

/**
 * Component to maintain Clients.
 */
@observer
export class AppClientEditor extends React.Component<AppClientEditorProps> {
  @observable fieldState = {
    name: newNonEmptyTextField(),
    domainName: newRegexTextFieldWithStopWords(
      // RE: Say 2 - 25 chars, start with letter, only letters, digits, underscores or hyphens.
      // This is more restrictive than the server which (since FF2-417) has the RE
      /// /[a-zA-Z0-9_-]+/ in IdentityService and Auth0Manager
      /^[A-Za-z][A-Za-z0-9_-]{1,24}$/,
      'Only letters, digits, hyphens or underscores (_) allowed. Must start with a letter. Minimum 2 and maximum 25 characters.',
      disallowedDomainNames
    ),
    logoUrl: newRegexTextField(
      /// This is a URL in the database but we should allow for upload as well
      /// FIXME this regex is not the best, a better one would be /^(http[s]?:(\/){2})([^\s\/]+\/).*$/ but
      /// grouping () does not seem to work
      /^http[s]?:\/\/[^\s\/]+\/?.*$/,
      'Only valid URLs allowed'
    ),

    loginBackgroundUrl: newRegexTextField(
      /// This is a URL in the database but we should allow for upload as well
      /// FIXME this regex is not the best, a better one would be /^(http[s]?:(\/){2})([^\s\/]+\/).*$/ but
      /// grouping () does not seem to work
      /^http[s]?:\/\/[^\s\/]+\/?.*$/,
      'Only valid URLs allowed'
    ),

    recommendationsVisible: true,

    providerIds: Array<DbKey<LearningProvider>>(),

    recreateUserQuizEnabled: false,

    walkthroughsEnabled: false,

    /// Walkthroughs - control whether they are only delivered once or on every visit
    skipIfAlreadySeen: true,

    debugReporting: false,

    displayHeadlineRatio: false,

    displayHeadlineText: true,

    globalUserGroup: false,

    // To tabs or not to tabs tath si the nesquoit
    singleTabRecommendations: false,

    /// Allow Self-Assessment (as against using Quiz)
    allowSelfAssessment: false,

    /// Only display assessments on work roles belonging to the client
    displayClientAssessmentsOnly: false
  };

  // These are no longer required except for display
  // @observable
  auth0Domain: string | null = null;
  //  = newRegexTextField(
  //   /^[^\s\/]+\/?.*$/,
  //   "Valid domain name required"
  // )
  // Not user-configurable - either not set or read-only
  auth0ClientId: string | null = null;

  restrictedFields: RestrictedFields = {};

  noShowFields = {};

  // Using SSO
  // TODO There's an opportunity to update this, but in practice until
  // we use the Management API to link or unlink connections in Auth0
  // in concert with changes here, all this will do is switch SSO on or
  // off. If the SSO connection isn't linked at Auth0, it won't
  // work by linking it here.
  @observable ssoConnection: string | null = null;

  /// Default repos
  @observable repository: RepositoryEntrySet;

  /// Uploaded icon file
  @observable newLogo?: SelectedFileSpec;
  @observable newBackground?: SelectedFileSpec;
  @observable loginBackgroundIsDefault: boolean = true;

  ssoConnections: WithDbId<Auth0SamlConnection>[] = [];

  /// Create Global group for new client?
  @observable createGlobalGroup: boolean = true;

  @observable locked = false;

  constructor(props: AppClientEditorProps) {
    super(props);
    this.repository = props.repository;
    if (props.item) {
      const { value } = props.item;
      const state = this.fieldState;
      state.name.onChange(value.name);
      state.domainName.onChange(value.domainName);
      state.logoUrl.onChange(value.logoUrl);
      this.loginBackgroundIsDefault = value.loginBackgroundUrl.kind === 'default';
      const loginBackgroundUrl =
        value.loginBackgroundUrl.kind === 'default' ? defaultLoginBackground : value.loginBackgroundUrl.value;
      state.loginBackgroundUrl.onChange(loginBackgroundUrl);
      state.recommendationsVisible = value.recommendationsVisible;
      state.recreateUserQuizEnabled = value.recreateUserQuizEnabled;
      state.providerIds = value.providerIds;
      state.walkthroughsEnabled = value.walkthroughsEnabled;
      state.skipIfAlreadySeen = value.skipIfAlreadySeen;
      this.auth0ClientId = value.auth0ClientId;
      this.auth0Domain = value.auth0Domain;
      state.debugReporting = value.debugReporting;
      state.displayHeadlineRatio = value.displayHeadlineRatio;
      state.displayHeadlineText = value.displayHeadlineText;
      state.singleTabRecommendations = value.singleTabRecommendations;
      state.allowSelfAssessment = value.allowSelfAssessment;
      state.displayClientAssessmentsOnly = value.displayClientAssessmentsOnly;
      // Load the client repos if there is one...
      this.loadClientRepository(props.repository, value.domainName);
      this.ssoConnection = value.connectionName;
      this.ssoConnections = this.props.ssoConnections.filter(cxn => cxn.value.domainName === this.auth0Domain);

      /// Handle client admin
      if (props.role === 'client-admin') {
        /// Display-only fields
        this.restrictedFields = {
          name: DisplayText({
            field: value.name,
            title: 'Name',
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          domainName: DisplayText({
            field: value.domainName,
            title: 'Domain name',
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          recommendationsVisible: DisplayCheckItem({
            field: value.recommendationsVisible,
            label: 'Recommendations Visible',
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          providers: MultiSelectContainer({
            options: [],
            selectedOptions:
              this.selectedProviderOptions(value.providerIds).length > 0
                ? this.selectedProviderOptions(value.providerIds)
                : [{ label: 'ERROR needs at least one Learning Provider', value: '' }],
            title: 'Recommendations Learning Providers',
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          displayHeadlineRatio: DisplayCheckItem({
            field: value.displayHeadlineRatio,
            label: "Display 'Headline' score in Quiz Results",
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          displayHeadlineText: DisplayCheckItem({
            field: value.displayHeadlineText,
            label: 'Display explanatory text (for percentages) in Quiz Result',
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          singleTabRecommendations: DisplayCheckItem({
            field: value.singleTabRecommendations,
            label: 'Use new enhanced Recommendations page with no tabs',
            xsSpan: 2,
            debug: !!this.props.debug
          }),
          allowSelfAssessment: DisplayCheckItem({
            field: value.allowSelfAssessment,
            label: 'Using Self-Assessments instead of Quizzes',
            xsSpan: 2,
            debug: !!this.props.debug
          })
        };
      }
    } else {
      // Use default repos if this is a 'New' client
      // --- [ FIXME ] --------------------------------------------------------
      // Say that the domain has already been set up in S3 - perhaps we created
      // and then deleted the client, but now we are re-creating a different one with the same
      // domain.
      //
      // In that scenario there's no way to check whether the relevant icon/site-image folders already
      // exist without reloading. This doesn't matter unless the domain is being reused for a
      // completely different client, and the previous client has either been deleted or is in
      // a different environment (eg. UAT vs PROD).
      // There's no real solution until clients have separate buckets.
      //
      // This problem also affects Auth0 - it may be unlikely that two different clients have
      // the same client name, but it is theoretically possible for two different clients to
      // have the same subdomain and therefore origin endpoints. The only thing stopping this is
      // the database unique constraints and the separation between DEV/UAT/PROD Auth0 tenants.
      //
      // --- [ OTOH... using client ID (say) doesn't work because... ] --------
      //
      // (1) For a new client, no client ID will yet exist
      //
      // (2) This would prevent us from taking a copy of the client
      //     (for support purposes), putting that into UAT or TEST, and using it straight away
      //     without having to physically copy the files in S3 (though if UAT were to
      //     use a different bucket that would be a problem anyway). Using the client domain
      //     name in the same bucket will let us use existing files immediately.
      //
      // This doesn't apply to Auth0 but if we copy all the Auth0 details as well,
      // the client copy in UAT/TEST will use the same Auth0 application.
      // ----------------------------------------------------------------------
      this.fieldState.logoUrl.onChange(defaultLogo);
      this.fieldState.loginBackgroundUrl.onChange(defaultLoginBackground);
      /// Default LP
      this.fieldState.providerIds = [defaultProviderId];
    }
  }

  @action lockUpdates = () => (this.locked = true);

  loadClientRepository = async (repository: RepositoryEntrySet, clientDomainName: string) => {
    if (this.props.onLoadClientRepository) {
      this.repository = await this.props.onLoadClientRepository(repository, clientDomainName);
    }
  };

  learningProviderOptions = (): SelectOption[] => {
    return this.props.learningProviders.map(provider => {
      return { label: provider.value.name, value: provider.id };
    });
  };

  ssoConnectionOptions = (): SelectOption[] => {
    const nullOption = { label: 'Not using SSO', value: '' };
    const connectionOptions = this.ssoConnections.map(cxn => ({ label: cxn.value.name, value: cxn.value.name }));
    return [nullOption, ...connectionOptions];
  };

  @action
  onSetConnection = (selectedCxn: SelectOption) => {
    selectedCxn.value !== '' ? (this.ssoConnection = `${selectedCxn.value}`) : (this.ssoConnection = null);
  };

  filteredConnectionOption = (cxn?: string): SelectOption => {
    const option = cxn ? this.ssoConnectionOptions().find(opt => opt.value === cxn) : undefined;
    return option === undefined || cxn === '' ? { label: 'Not Using SSO', value: '' } : option;
  };

  @action
  onSetProviders = (selectedOptions: SelectOption[]) => {
    selectedOptions.length === 0
      ? (this.fieldState.providerIds = [])
      : (this.fieldState.providerIds = selectedOptions.map(option => `${option.value}`));
  };

  selectedProviderOptions = (providerIds: DbKey<LearningProvider>[]): SelectOption[] => {
    const options: SelectOption[] = [];
    providerIds.forEach(id => {
      const option = this.learningProviderOptions().find(opt => opt.value === id);
      if (option !== undefined) {
        options.push(option);
      }
    });
    return options;
  };

  ProviderObserver = observer(() => {
    return MultiSelectObserver({
      options: this.learningProviderOptions(),
      selectedOptions: this.selectedProviderOptions(this.fieldState.providerIds),
      title: 'Learning Providers to filter recommendations',
      xsSpan: 2,
      onChange: this.onSetProviders,
      debug: !!this.props.debug
    });
  });

  /// --- [ FIXME ] ---------------------
  /// Needs updateAuth0 client if changed
  NameObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.name,
      type: 'text',
      title: 'Name (eg. Acme Company, Inc.)',
      placeholder: 'Your Site Name',
      xsSpan: 2,
      debug: this.props.debug
    })
  );

  /// --- [ FIXME ] ---------------------
  /// Needs updateAuth0 client if changed
  DomainNameObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.domainName,
      type: 'text',
      title: 'Domain Name (eg. acme_company)',
      placeholder: 'your_client_domain',
      xsSpan: 2,
      debug: this.props.debug
    })
  );

  /// --- [ FIXME ] ---------------------
  /// Needs updateAuth0 client if changed
  LogoUrlObserver = observer(() =>
    ImgObserver({
      field: this.fieldState.logoUrl,
      type: 'text',
      title: 'Current Logo URL',
      placeholder: '',
      xsSpan: 3,
      debug: this.props.debug
    })
  );

  LoginBackgroundUrlObserver = observer(() => {
    return this.loginBackgroundIsDefault
      ? null
      : ImgObserver({
        field: this.fieldState.loginBackgroundUrl,
        type: 'text',
        title: 'Current Login Background URL',
        placeholder: '',
        xsSpan: 3,
        debug: this.props.debug
      });
  });

  RadioDefaultBackgroundObserver = observer(() => (
    <RadioItem
      label="Default Login Background Image"
      onChange={this.onChangeLoginBackground('default')}
      selected={this.loginBackgroundIsDefault}
      size={'xxsmall'}
    />
  ));

  RadioSpecifiedBackgroundObserver = observer(() => (
    <RadioItem
      label="Custom Login Background Image"
      onChange={this.onChangeLoginBackground('url')}
      selected={!this.loginBackgroundIsDefault}
      size={'xxsmall'}
    />
  ));

  onChangeLoginBackground = (radio: 'default' | 'url') => (value: boolean) => {
    if (value) {
      switch (radio) {
        case 'default':
          runInAction(() => {
            this.loginBackgroundIsDefault = true;
            this.fieldState.loginBackgroundUrl.onChange(defaultLoginBackground);
          });
          break;
        case 'url':
          runInAction(() => (this.loginBackgroundIsDefault = false));
      }
    }
  };

  /// Only show up on New form
  CreateGlobalGroupObserver = observer(() =>
    CheckItemObserver({
      field: this.createGlobalGroup,
      label: 'Create Global Group?',
      xsSpan: 2,
      onChange: this.toggleCreateGlobalGroup,
      debug: this.props.debug
    })
  );

  RecommendationsVisibleObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.recommendationsVisible,
      label: 'Recommendations Visible',
      xsSpan: 2,
      onChange: this.toggleRecommendations,
      debug: this.props.debug
    })
  );

  RecreateUserQuizEnabledObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.recreateUserQuizEnabled,
      label: 'Automatic Reassess Enabled',
      xsSpan: 2,
      onChange: this.toggleRecreateUserQuiz,
      debug: this.props.debug
    })
  );

  WalkthroughsEnabledObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.walkthroughsEnabled,
      label: 'Walkthroughs enabled',
      xsSpan: 2,
      onChange: this.toggleWalkthroughsEnabled,
      debug: this.props.debug
    })
  );

  SkipIfAlreadySeenObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.skipIfAlreadySeen,
      label: 'Walkthroughs on first visit only',
      xsSpan: 2,
      onChange: this.toggleSkipIfAlreadySeen,
      debug: this.props.debug
    })
  );

  DebugReportingEnabledObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.debugReporting,
      label: 'Show scores not percentages in results',
      xsSpan: 2,
      onChange: this.toggleDebugReporting,
      debug: this.props.debug
    })
  );

  DisplayHeadlineRatioObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.displayHeadlineRatio,
      label: "Display 'Headline' score in Quiz Results",
      xsSpan: 2,
      onChange: this.toggleDisplayHeadlineRatio,
      debug: this.props.debug
    })
  );

  DisplayHeadlineTextObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.displayHeadlineText,
      label: 'Display explanatory text (for percentages) in Quiz Results',
      xsSpan: 2,
      onChange: this.toggleDisplayHeadlineText,
      debug: this.props.debug
    })
  );

  TabbedRecommendationsObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.singleTabRecommendations,
      label: 'Recommendations page with no tabs (off = tabbed on Category)',
      xsSpan: 2,
      onChange: this.toggleTabbedRecommendations,
      debug: this.props.debug
    })
  );

  AllowSelfAssessmentObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.allowSelfAssessment,
      label: 'Using Self-Assessments instead of Quizzes',
      xsSpan: 2,
      onChange: this.toggleAllowSelfAssessment,
      debug: this.props.debug
    })
  );

  DisplayClientAssessmentsOnlyObserver = observer(() =>
    CheckItemObserver({
      field: this.fieldState.displayClientAssessmentsOnly,
      label: 'Only display assessments generated from Roles owned by this client',
      xsSpan: 2,
      onChange: this.toggleDisplayClientAssessmentsOnly,
      debug: this.props.debug
    })
  );

  ConnectionObserver = () => {
    return SelectObserver({
      options: this.ssoConnectionOptions(),
      selectedOption: this.filteredConnectionOption(),
      title: 'SSO Connection',
      xsSpan: 2,
      onChange: this.onSetConnection,
      debug: false
    });
  };

  @action
  toggleRecommendations = () => {
    this.fieldState.recommendationsVisible = !this.fieldState.recommendationsVisible;
  };

  @action
  toggleTabbedRecommendations = () => {
    this.fieldState.singleTabRecommendations = !this.fieldState.singleTabRecommendations;
  };

  @action
  toggleRecreateUserQuiz = () => {
    this.fieldState.recreateUserQuizEnabled = !this.fieldState.recreateUserQuizEnabled;
  };

  @action
  toggleWalkthroughsEnabled = () => {
    this.fieldState.walkthroughsEnabled = !this.fieldState.walkthroughsEnabled;
  };

  @action
  toggleSkipIfAlreadySeen = () => {
    this.fieldState.skipIfAlreadySeen = !this.fieldState.skipIfAlreadySeen;
  };

  @action
  toggleDebugReporting = () => {
    this.fieldState.debugReporting = !this.fieldState.debugReporting;
  };

  @action
  toggleDisplayHeadlineRatio = () => {
    this.fieldState.displayHeadlineRatio = !this.fieldState.displayHeadlineRatio;
  };

  @action
  toggleDisplayHeadlineText = () => {
    this.fieldState.displayHeadlineText = !this.fieldState.displayHeadlineText;
  };

  @action
  toggleCreateGlobalGroup = () => {
    this.createGlobalGroup = !this.createGlobalGroup;
  };

  @action
  toggleAllowSelfAssessment = () => {
    this.fieldState.allowSelfAssessment = !this.fieldState.allowSelfAssessment;
  };

  @action
  toggleDisplayClientAssessmentsOnly = () =>
    (this.fieldState.displayClientAssessmentsOnly = !this.fieldState.displayClientAssessmentsOnly);

  @computed
  get inputIsValid(): boolean {
    const { name, domainName, logoUrl, loginBackgroundUrl, providerIds } = this.fieldState;
    return (
      name.getValid() !== undefined &&
      domainName.getValid() !== undefined &&
      logoUrl.getValid() !== undefined &&
      loginBackgroundUrl.getValid() !== undefined &&
      providerIds.length > 0 &&
      this.authFieldsValid
    );
  }

  // Just for safety we want to ensure auth0 is write-once
  @computed
  get authFieldsValid(): boolean {
    if (this.props.item) {
      return (
        this.auth0ClientId === this.props.item.value.auth0ClientId &&
        this.auth0Domain === this.props.item.value.auth0Domain
      );
    } else {
      return this.auth0ClientId === null && this.auth0Domain === null;
    }
  }

  /// Dig out the RepositoryEntries for the client's logo files/background images if it exists.
  /// If not, or if this is a new client, return the default (which is skillseeker/icons
  /// and should always be set).
  /// --- [ NB At present this returns undefined if there is no existing repo ] ---
  /// Therefore should not assume on upload that there is one
  getRepo = (repoType: 'icons' | 'site-images'): RepositoryEntries | undefined => {
    const { domainName } = this.fieldState;

    if (domainName.getValid() !== undefined) {
      return FileUtility.getClientRepo(repoType, domainName.value(), this.repository);
    }
    return undefined;
  };

  onLogoSelect = (newFile: SelectedFileSpec): void => {
    switch (newFile.kind) {
      // ignore "default"
      case 'default':
        return;
      case 'repositoryFile':
        this.fieldState.logoUrl.onChange(newFile.uri);
        break;
      // Not implemented
      case 'remoteFile':
        this.fieldState.logoUrl.onChange(newFile.uri);
        break;
      case 'upload': {
        // Save for onSave to handle
        this.newLogo = newFile;
      }
    }
  };

  onBackgroundSelect = (newFile: SelectedFileSpec): void => {
    switch (newFile.kind) {
      // ignore "default"
      case 'default':
        return;
      case 'repositoryFile':
        this.fieldState.loginBackgroundUrl.onChange(newFile.uri);
        break;
      // Not implemented
      case 'remoteFile':
        this.fieldState.loginBackgroundUrl.onChange(newFile.uri);
        break;
      case 'upload': {
        // Save for onSave to handle
        this.newBackground = newFile;
      }
    }
  };

  onUploadToRepo = async (newFile: SelectedFileSpec): Promise<string | undefined> => {
    const result = await FileUtility.uploadRepoFile(newFile, this.props.onUpload);
    return result;
  };

  onSave = async () => {
    const { item, onCreate, onUpdate } = this.props;

    this.lockUpdates();

    /// Look for uploaded files...
    if (this.newLogo) {
      const newLogoUrl = await this.onUploadToRepo(this.newLogo);
      if (newLogoUrl) {
        this.fieldState.logoUrl.onChange(newLogoUrl);
      }
    }
    if (this.newBackground) {
      const newBackgroundUrl = await this.onUploadToRepo(this.newBackground);
      if (newBackgroundUrl) {
        this.fieldState.loginBackgroundUrl.onChange(newBackgroundUrl);
      }
    }

    const backgroundUrl: BrandingUrl =
      this.fieldState.loginBackgroundUrl.value().localeCompare(defaultLoginBackground) === 0 ||
        this.loginBackgroundIsDefault
        ? { kind: 'default' }
        : { kind: 'url', value: this.fieldState.loginBackgroundUrl.value() };

    /// Build from scratch here to get latest changes
    const ac = makeAppClient({
      name: this.fieldState.name.value(),
      domainName: this.fieldState.domainName.value(),
      logoUrl: this.fieldState.logoUrl.value(),
      loginBackgroundUrl: backgroundUrl,
      recommendationsVisible: this.fieldState.recommendationsVisible,
      recreateUserQuizEnabled: this.fieldState.recreateUserQuizEnabled,
      walkthroughsEnabled: this.fieldState.walkthroughsEnabled,
      skipIfAlreadySeen: this.fieldState.skipIfAlreadySeen,
      providerIds:
        this.fieldState.providerIds.length === 1 && this.fieldState.providerIds[0] === ''
          // This may no longer be fit for purpose - we want to prevent saving with an empty provider list
          // ? []
          ? [defaultProviderId]
          : this.fieldState.providerIds,
      debugReporting: this.fieldState.debugReporting,
      displayHeadlineRatio: this.fieldState.displayHeadlineRatio,
      displayHeadlineText: this.fieldState.displayHeadlineText,
      singleTabRecommendations: this.fieldState.singleTabRecommendations,
      allowSelfAssessment: this.fieldState.allowSelfAssessment,
      displayClientAssessmentsOnly: this.fieldState.displayClientAssessmentsOnly,
      // May be null on create
      auth0Domain: this.auth0Domain === null ? '' : this.auth0Domain,
      auth0ClientId: this.auth0ClientId === null ? '' : this.auth0ClientId,
      connectionName: this.ssoConnection
    });

    if (item && onUpdate) {
      await onUpdate(
        makeWithDbId({
          id: item.id,
          value: { ...ac }
        })
      );
    } else {
      await onCreate(ac, this.createGlobalGroup);
    }
  };

  EditDetails = () => (
    <React.Fragment>
      <h4>{this.props.role === 'super' ? 'Client Details' : 'Site Details'}</h4>
      <div className={styles.row}>
        {this.props.role === 'super' ? <this.NameObserver /> : this.restrictedFields.name}
        {this.props.role === 'super' ? <this.DomainNameObserver /> : this.restrictedFields.domainName}
      </div>
      <div className={styles.row}>
        {this.props.role === 'super' && this.props.item === undefined && <this.CreateGlobalGroupObserver />}
      </div>
      <div className={classnames(styles.row, styles.checkItemSet)}>
        {this.props.role === 'super' ? (
          <this.RecommendationsVisibleObserver />
        ) : (
          this.restrictedFields.recommendationsVisible
        )}
        {this.props.role === 'super' && <this.RecreateUserQuizEnabledObserver />}
        <this.WalkthroughsEnabledObserver />
        <this.SkipIfAlreadySeenObserver />
        {this.props.role === 'super' && <this.DebugReportingEnabledObserver />}
        {this.props.role === 'super' ? (
          <this.DisplayHeadlineRatioObserver />
        ) : (
          this.restrictedFields.displayHeadlineRatio
        )}
        {this.props.role === 'super' ? <this.DisplayHeadlineTextObserver /> : this.restrictedFields.displayHeadlineText}
        {this.props.role === 'super' && <this.TabbedRecommendationsObserver />}
        {this.props.role === 'super' ? <this.AllowSelfAssessmentObserver /> : this.restrictedFields.allowSelfAssessment}
        <this.DisplayClientAssessmentsOnlyObserver />
      </div>
      <div className={styles.row}>
        {this.props.role === 'super' ? <this.ProviderObserver /> : this.restrictedFields.providers}
        <this.ConnectionObserver />
      </div>
    </React.Fragment>
  );

  EditLogo = () => {
    const { logoUrl } = this.fieldState;

    const logoFiles = this.getRepo('icons');

    return (
      <div className={styles.row}>
        <h4>{this.props.role === 'super' ? 'Client Logo' : 'Site Logo'}</h4>
        <ImageFileManager
          title={'Logo'}
          repositoryEntries={logoFiles}
          defaultUrl={logoUrl.value()}
          onChange={this.onLogoSelect}
        />
      </div>
    );
  };

  EditLoginBackground = observer(() => {
    if (this.loginBackgroundIsDefault) {
      return null;
    }

    const { loginBackgroundUrl } = this.fieldState;

    const loginBackgrounds = this.getRepo('site-images');

    return (
      <ImageFileManager
        title={'Login Background'}
        repositoryEntries={loginBackgrounds}
        defaultUrl={loginBackgroundUrl.value()}
        // isValid={}
        // onCancel={}
        onChange={this.onBackgroundSelect}
      />
    );
  });

  render() {
    return (
      <div className={styles.page}>
        {renderButtons(
          this.onSave,
          undefined,
          this.props.onCancel,
          () => this.inputIsValid && !this.locked,
          undefined,
          () => !this.locked
        )}
        <div className={styles.body}>
          <this.EditDetails />
        </div>
        <div className={styles.body}>
          <this.EditLogo />
        </div>
        <div className={styles.body}>
          <h4>{this.props.role === 'super' ? 'Client Login Background' : 'Site Login Background'}</h4>
          <div className={styles.row}>
            <this.RadioDefaultBackgroundObserver />
          </div>
          <div className={styles.row}>
            <this.RadioSpecifiedBackgroundObserver />
            <this.EditLoginBackground />
          </div>
        </div>
      </div>
    );
  }
}

///
/// Should be disabled in production
/// TODO if enabled delete Auth0Client too
///
export interface ClientDeleterProps {
  items?: WithDbId<AppClient>[];
  onDelete?(ids: DbKey<AppClient>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to delete AppClient */
// @observer
export class ClientDeleter extends React.Component<ClientDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Clients', items.map(item => item.value.name))}
          </div>
        </div>
      </div>
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/appclient-editor/appclient-editor.tsx